import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { publicApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Clock, Zap, RefreshCw } from 'lucide-react';

export default function PendingPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const ref = searchParams.get('ref');
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState('pending');
  const pollRef = useRef(null);
  const pollCount = useRef(0);

  const checkStatus = async () => {
    if (!orderId) return;
    setChecking(true);
    try {
      const res = await publicApi.getPaymentStatus(orderId);
      const s = res.data.payment?.status;
      setStatus(s);
      if (s === 'paid') navigate(`/checkin/success?ref=${ref}&order_id=${orderId}`);
      else if (s === 'failed' || s === 'cancelled') navigate(`/checkin/failure?ref=${ref}&order_id=${orderId}`);
    } catch {}
    finally { setChecking(false); }
  };

  useEffect(() => {
    pollRef.current = setInterval(() => {
      pollCount.current += 1;
      if (pollCount.current <= 10) checkStatus();
      else clearInterval(pollRef.current);
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [orderId]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }}>
      <div className="max-w-[440px] mx-auto px-4 pt-12 pb-8">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-7 h-7 rounded bg-gradient-to-br from-[#D4A843] to-[#B8923A] flex items-center justify-center">
            <Zap size={13} className="text-[#120F08]" />
          </div>
          <span className="font-['EB_Garamond'] text-[#F2F2F2] text-lg font-semibold">Scratch Unleashed</span>
        </div>

        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full border-2 border-[#E6B450] flex items-center justify-center mx-auto mb-4">
            <Clock size={32} className="text-[#E6B450]" />
          </div>
          <h1 className="font-['EB_Garamond'] text-3xl text-[#F2F2F2] mb-1">Payment Pending</h1>
          <p className="text-[#B9B9B9] text-sm">UPI confirmations can take a few minutes.</p>
        </div>

        <div className="surface-card card-highlight rounded-xl p-5 space-y-3 mb-6">
          {ref && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#B9B9B9]">Booking Ref</span>
              <span className="text-mono text-[#D4A843]">{ref}</span>
            </div>
          )}
          {orderId && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#B9B9B9]">Order ID</span>
              <span className="text-mono text-[#6F6F6F] text-xs">{orderId}</span>
            </div>
          )}
          <div className="bg-[#1F1A10] border border-[#3A2F16] rounded-lg p-3">
            <p className="text-[#F1E2B8] text-sm">We're automatically checking your payment status every 5 seconds.</p>
          </div>
        </div>

        <div className="space-y-3">
          <Button
            onClick={checkStatus}
            disabled={checking}
            className="w-full btn-gold rounded-xl h-11 font-semibold flex items-center gap-2"
          >
            {checking ? <div className="w-4 h-4 border-2 border-[#120F08] border-t-transparent rounded-full animate-spin" /> : <RefreshCw size={15} />}
            Check Status Now
          </Button>
          <Button
            onClick={() => navigate('/checkin')}
            variant="outline"
            className="w-full rounded-xl h-11 bg-[#1A1A1A] border-[#2A2A2A] text-[#F2F2F2]"
          >
            Back to Check-in
          </Button>
        </div>
      </div>
    </div>
  );
}
